import { useQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/lib/axios'
import type {
  CompanyBillingStats,
  DriverBillingStats,
  UserBillingStats,
  BillingPeriod,
  BillingDriverEvent,
  BillingDriverGroup,
  BillingSummaryDriver,
} from '../types'

interface BillingSummaryDto {
  driverGroups?: Record<BillingDriverGroup, BillingSummaryDriver[]>
  companyId: string
  period: string
  activeDriversCount: number | null
  driversWithContractCount: number | null
  driversWithTimesheetCalculated: number | null
  driversCalculatedInMonth: number | null
  driversWithoutActivityCount: number | null
  driversWithZeroPayoutCount: number | null
  driversWithOnlyLeaveEquivalentCount: number | null
  timesheetCalculationsCount: number | null
}

interface DriverBillingStatsDto {
  driverId: number
  wasActive: boolean
  timesheetCalculationsCount: number
  timesheetRecalculationsCount: number
  reportingMonthsCovered: number
  importedActivityDays: number
  printoutsGeneratedCount: number
  lastEventAt: string | null
  lastEventByUserId: string | null
  lastEventByUserName: string | null
  lastEventByUserEmail: string | null
}

interface BillingUserStatsDto {
  userId: string
  userDisplayName: string
  userEmail: string | null
  eventsCount: number
  driversTouched: number
  timesheetCalculationsCount: number
  importsCount: number
  printoutsCount: number
}

interface BillingEventDto {
  id: string
  eventType: string
  occurredAt: string
  dataPeriodStart: string | null
  dataPeriodEnd: string | null
  triggeredByUserId: string | null
  triggeredByUserName: string | null
  triggeredByUserEmail: string | null
  source: string
}

/**
 * Per-company stats across every child company under the tenant.
 * Used by the (future) cross-company billing screen.
 *
 * TODO: wire up to the backend ledger endpoint. Returns [] until then so the
 * column scaffold renders with "-" placeholders.
 */
export function useQueryBillingStats(
  tenantId: number | null,
  period: BillingPeriod,
) {
  return useQuery<CompanyBillingStats[]>({
    queryKey: ['billing.stats.companies', tenantId, period],
    queryFn: async (): Promise<CompanyBillingStats[]> => {
      return []
    },
    enabled: tenantId != null && !!period,
    staleTime: 30_000,
  })
}

/**
 * Single-company KPIs. Calculated drivers are counted from prepared records
 * for the selected data month, including inactive drivers. The per-driver
 * operations table is independently grouped by operation date.
 */
export function useQueryCompanyBillingSummary(
  companyId: string | null,
  period: BillingPeriod,
) {
  return useQuery<CompanyBillingStats | null>({
    queryKey: ['billing.stats.company', companyId, period],
    queryFn: async (): Promise<CompanyBillingStats | null> => {
      const { data } = await axiosInstance.get<BillingSummaryDto>(
        `/api/${companyId}/billing/summary`,
        { params: { period } },
      )
      // Server returns null for KPIs not yet wired to the tenant DB. Mirror
      // those nulls onto the CompanyBillingStats shape; the UI renders "-"
      // when a field is missing.
      return {
        companyId: data.companyId,
        driverGroups: data.driverGroups,
        activeDriversCount: data.activeDriversCount ?? (undefined as any),
        driversWithContractCount: data.driversWithContractCount ?? (undefined as any),
        driversWithTimesheetCalculated:
          data.driversWithTimesheetCalculated ?? (undefined as any),
        driversCalculatedInMonth: data.driversCalculatedInMonth ?? undefined,
        driversWithoutActivityCount: data.driversWithoutActivityCount ?? undefined,
        driversWithZeroPayoutCount: data.driversWithZeroPayoutCount ?? undefined,
        driversWithOnlyLeaveEquivalentCount: data.driversWithOnlyLeaveEquivalentCount ?? undefined,
        timesheetCalculationsCount: data.timesheetCalculationsCount ?? (undefined as any),
        // Legacy fields not used by the trimmed UI; populated by null so the
        // `fmt` helper renders "-".
        timesheetRecalculationsCount: undefined as any,
        reportingMonthsCovered: undefined as any,
        importedFilesCount: undefined as any,
        importedActivityDays: undefined as any,
        printoutsGeneratedCount: undefined as any,
        exportsGeneratedCount: undefined as any,
        driversAddedCount: undefined as any,
        workingUsersCount: undefined as any,
      }
    },
    enabled: companyId != null && !!period,
    staleTime: 30_000,
  })
}

/**
 * Per-driver breakdown for one company × period.
 *
 * Backend returns rows only for drivers with at least one event in the
 * period; the table merges these against the tenant's full driver roster.
 */
export function useQueryCompanyBillingDrivers(
  companyId: string | null,
  period: BillingPeriod,
) {
  return useQuery<DriverBillingStats[]>({
    queryKey: ['billing.stats.drivers', companyId, period],
    queryFn: async (): Promise<DriverBillingStats[]> => {
      const { data } = await axiosInstance.get<DriverBillingStatsDto[]>(
        `/api/${companyId}/billing/drivers`,
        { params: { period } },
      )
      return data.map((d) => ({
        driverId: d.driverId,
        wasActive: d.wasActive,
        timesheetCalculationsCount: d.timesheetCalculationsCount,
        timesheetRecalculationsCount: d.timesheetRecalculationsCount,
        reportingMonthsCovered: d.reportingMonthsCovered,
        importedActivityDays: d.importedActivityDays,
        printoutsGeneratedCount: d.printoutsGeneratedCount,
        lastEventAt: d.lastEventAt,
        lastEventByUserId: d.lastEventByUserId,
        lastEventByUserName: d.lastEventByUserName,
        lastEventByUserEmail: d.lastEventByUserEmail,
      }))
    },
    enabled: companyId != null && !!period,
    staleTime: 30_000,
  })
}

/**
 * Per-user breakdown — who inside the org produced billable events for this
 * company in the period. Background-job events are excluded server-side, so
 * the rows here are strictly humans.
 */
export function useQueryCompanyBillingUsers(
  companyId: string | null,
  period: BillingPeriod,
) {
  return useQuery<UserBillingStats[]>({
    queryKey: ['billing.stats.users', companyId, period],
    queryFn: async (): Promise<UserBillingStats[]> => {
      const { data } = await axiosInstance.get<BillingUserStatsDto[]>(
        `/api/${companyId}/billing/users`,
        { params: { period } },
      )
      return data.map((u) => ({
        userId: u.userId,
        userDisplayName: u.userDisplayName,
        userEmail: u.userEmail,
        eventsCount: u.eventsCount,
        driversTouched: u.driversTouched,
        timesheetCalculationsCount: u.timesheetCalculationsCount,
        importsCount: u.importsCount,
        printoutsCount: u.printoutsCount,
      }))
    },
    enabled: companyId != null && !!period,
    staleTime: 30_000,
  })
}

/**
 * Raw event timeline for one driver × period — drives the accordion that
 * expands under a driver row. Each event already carries its resolved user
 * display name + email so the row template doesn't need a separate lookup.
 *
 * Lazy by design: the hook stays disabled until `enabled` flips true, which
 * the row component sets when the accordion expands.
 */
export function useQueryDriverBillingEvents(
  companyId: string | null,
  driverId: number | null,
  period: BillingPeriod,
  enabled: boolean,
) {
  return useQuery<BillingDriverEvent[]>({
    queryKey: ['billing.events.driver', companyId, driverId, period],
    queryFn: async (): Promise<BillingDriverEvent[]> => {
      const { data } = await axiosInstance.get<BillingEventDto[]>(
        `/api/${companyId}/billing/drivers/${driverId}/events`,
        { params: { period } },
      )
      return data.map((e) => ({
        id: e.id,
        eventType: e.eventType,
        occurredAt: e.occurredAt,
        dataPeriodStart: e.dataPeriodStart,
        dataPeriodEnd: e.dataPeriodEnd,
        triggeredByUserId: e.triggeredByUserId,
        triggeredByUserName: e.triggeredByUserName,
        triggeredByUserEmail: e.triggeredByUserEmail,
        source: e.source,
      }))
    },
    enabled: enabled && companyId != null && driverId != null && !!period,
    staleTime: 30_000,
  })
}
